import { useCallback, useEffect, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bot, EyeOff, Loader2, Send, ShieldCheck, Users } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  legalMailAutopilotGet, legalMailAutopilotPut, legalMailAutopilotSweep, legalMailHeld, legalMailHeldRelease, legalMailHeldSendPrivate,
} from "@/lib/api";
import { errorDetailMessage } from "@/lib/errorDetailMessage";

const MODES = [
  { id: "off", label: "Off", hint: "badge-only, as before" },
  { id: "dry_run", label: "Dry run", hint: "logs what it would do, moves nothing" },
  { id: "on", label: "On", hint: "quarantine → forward private post to you → release customer post" },
];
const ROUTE_TONE = {
  private: "bg-rose-100 text-rose-800 border-rose-300", collections: "bg-emerald-100 text-emerald-800 border-emerald-300",
  hold: "bg-amber-100 text-amber-800 border-amber-300", junk: "bg-slate-100 text-slate-600 border-slate-300",
};
const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";

/** Dean (9 Oct 2026): the Postbox privacy autopilot — admin only. */
export function MailAutopilotButton({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => legalMailAutopilotGet().then(setData).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const s = data?.settings;

  const save = async (patch) => {
    setBusy("save");
    try {
      const r = await legalMailAutopilotPut({ mode: s?.mode || "off", ...patch });
      setData(d => ({ ...d, settings: r.settings }));
      toast.success(`Postbox autopilot: ${r.settings.mode === "on" ? "ON" : r.settings.mode === "dry_run" ? "dry run" : "off"}`);
      onChanged?.();
    } catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(""); }
  };
  const sweep = async () => {
    if (!window.confirm("Quarantine everything in the Inbox now and re-read it with the privacy rules? The team will see customer post again within a few minutes.")) return;
    setBusy("sweep");
    try { const r = await legalMailAutopilotSweep(); toast.success(`${r.quarantined} scan(s) quarantined — routing over the next few minutes`); load(); onChanged?.(); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(""); }
  };

  const mode = s?.mode || "off";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={cn("h-8 text-xs", mode === "on" ? "border-emerald-400 text-emerald-800 bg-emerald-50" : mode === "dry_run" ? "border-amber-300 text-amber-800 bg-amber-50" : "")} data-testid="mail-autopilot-btn">
          <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />Privacy autopilot · {mode === "on" ? "ON" : mode === "dry_run" ? "dry run" : "off"}
          {!!data?.held && <Badge className="ml-1.5 h-4 px-1 text-[9px] bg-amber-500 hover:bg-amber-500">{data.held} held</Badge>}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[480px] p-0" data-testid="mail-autopilot-popover">
        <div className="px-4 py-3 border-b">
          <div className="text-sm font-semibold flex items-center gap-2"><Bot className="w-4 h-4 text-violet-600" />Postbox privacy autopilot</div>
          <p className="text-[11px] text-muted-foreground mt-1">Every new scan is moved out of the team's Inbox <b>before</b> it is read. HMRC, DWP, bank / finance, insurance, regulatory and personal post is forwarded to you and filed to Done — no #legal ping. Court and collections post about a customer goes back to the Inbox. Anything unreadable or doubtful is <b>held</b> for you.</p>
        </div>
        <div className="px-4 py-3 space-y-3">
          <div className="flex rounded-lg border overflow-hidden" data-testid="mail-autopilot-mode">
            {MODES.map(m => (
              <button key={m.id} type="button" title={m.hint} disabled={!!busy} onClick={() => m.id !== mode && save({ mode: m.id })}
                className={cn("flex-1 px-3 py-1.5 text-xs font-medium", mode === m.id ? (m.id === "on" ? "bg-emerald-600 text-white" : m.id === "dry_run" ? "bg-amber-500 text-white" : "bg-slate-800 text-white") : "bg-white text-slate-600 hover:bg-slate-50")}
                data-testid={`mail-autopilot-mode-${m.id}`}>{m.label}</button>
            ))}
          </div>
          {s && !s.live_actions && mode === "on" && <div className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1" data-testid="mail-autopilot-preview-note">Preview host — decisions are logged only; mail is moved and forwarded on production.</div>}
          <label className="text-xs block space-y-1">
            <span className="text-muted-foreground">Forward private post to</span>
            <Input defaultValue={s?.forward_to || ""} key={s?.forward_to} className="h-8 text-xs" data-testid="mail-autopilot-forward-to"
              onBlur={(e) => e.target.value && e.target.value !== s?.forward_to && save({ forward_to: e.target.value })} />
          </label>
          <label className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground">Slack DM me a one-line summary for each forward / hold</span>
            <Switch checked={s?.slack_dm !== false} onCheckedChange={(v) => save({ slack_dm: v })} className="scale-75" data-testid="mail-autopilot-dm-switch" />
          </label>
          <div className="flex items-center justify-between text-[11px] text-muted-foreground">
            <span>{data?.quarantined || 0} in quarantine · {data?.held || 0} held for you</span>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" disabled={!!busy || mode !== "on" || !s?.live_actions} onClick={sweep} title="Quarantine everything currently in the Inbox and re-read it" data-testid="mail-autopilot-sweep-btn">
              {busy === "sweep" ? <Loader2 className="w-3 h-3 animate-spin" /> : <EyeOff className="w-3 h-3 mr-1" />}Sweep inbox now
            </Button>
          </div>
        </div>
        <div className="border-t max-h-56 overflow-y-auto" data-testid="mail-autopilot-log">
          <div className="px-4 py-1.5 text-[10px] uppercase tracking-wide text-slate-400 bg-slate-50">Recent decisions</div>
          {(data?.log || []).filter(l => l.route).length === 0 && <div className="px-4 py-3 text-xs text-muted-foreground">No decisions yet — switch to <b>Dry run</b> and watch this list for a few days.</div>}
          {(data?.log || []).filter(l => l.route).map((l, i) => (
            <div key={i} className="px-4 py-1.5 border-t text-[11px] flex items-start gap-2">
              <Badge variant="outline" className={cn("text-[9px] px-1 py-0 shrink-0", ROUTE_TONE[l.route])}>{l.event === "dry_run" ? `would ${l.route}` : l.route}</Badge>
              <div className="min-w-0">
                <div className="truncate"><b>{l.letter_type}</b>{l.sender_org ? ` · ${l.sender_org}` : ""} <span className="text-slate-400">· {fmt(l.at)}</span></div>
                <div className="text-slate-500 truncate" title={l.summary}>{l.reason}{l.summary ? ` — ${l.summary}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

/** Dean's "Held" tab — scans the autopilot would not show the team. */
export function HeldPostList({ onChanged }) {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(() => legalMailHeld().then(r => setItems(r.items || [])).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);
  const act = async (imid, fn, label) => {
    setBusy(imid + label);
    try { const r = await fn(imid); toast.success(label === "release" ? "Released to the team" : `Forwarded to ${r.forwarded_to || "you"}`); load(); onChanged?.(); }
    catch (e) { toast.error(errorDetailMessage(e, e.message)); } finally { setBusy(""); }
  };
  if (items === null) return <div className="p-10 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>;
  if (!items.length) return <div className="p-10 text-center text-sm text-muted-foreground" data-testid="mail-held-empty">Nothing held — every scan has been routed.</div>;
  return (
    <div className="divide-y" data-testid="mail-held-list">
      {items.map(h => {
        const t = h.triage || {};
        return (
          <div key={h.internet_message_id} className="px-3 py-2.5 space-y-1" data-testid="mail-held-row">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-semibold truncate">{t.sender_org || h.subject || "Scanned post"}</span>
              <span className="text-[10px] text-muted-foreground shrink-0">{fmt(h.at)}</span>
            </div>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 inline-block">{h.reason}</div>
            {t.summary && <div className="text-xs text-slate-600">{t.summary}</div>}
            <div className="flex items-center gap-1.5 pt-1">
              <Button size="sm" variant="outline" className="h-7 text-[11px] text-emerald-800 border-emerald-300" disabled={!!busy} onClick={() => act(h.internet_message_id, legalMailHeldRelease, "release")} data-testid="mail-held-release-btn">
                <Users className="w-3 h-3 mr-1" />Release to team
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-[11px] text-rose-800 border-rose-300" disabled={!!busy} onClick={() => act(h.internet_message_id, legalMailHeldSendPrivate, "private")} data-testid="mail-held-private-btn">
                <Send className="w-3 h-3 mr-1" />Send to me
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
